import { FormControl, Grid, TextField } from '@mui/material';
import * as fns from 'date-fns';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import DatePicker from '../DatePicker';
import DefaultButton from '../FormControl/DefaultButton';
import ErrorButton from '../FormControl/ErrorButton';
import { FormValidationSchema } from '../FormControl/Form/Form';
import FormDialog from '../FormControl/FormDialog';
import TimePicker from '../TimePicker';
import User from '../../model/User';
import { useAuthentication } from '../../reducer/authentication';
import SelectSearchMultiAsync from '../SelectSearch/SelectSearchMultiAsync';
import OptionItem from '../SelectSearch/OptionItem';
import React, { useEffect, useState } from 'react';
import { ServiceApi, WhereCondition } from '../../service/ServiceApi';
import UserType from '../../enum/UserType';

export interface ReminderFormDialogData {
    comment: string;
    reminder_date: Date | null;
    user_ids: OptionItem<number>[];
}

interface ReminderFormDialogDataFull extends ReminderFormDialogData {
    reminder_time: string;
}

export interface ReminderFormDialogProps {
    reminderDialogOpen: boolean;
    onSubmit: (comment: string, reminder_date: Date, reminderUsers: OptionItem<number>[]) => any;
    onCancel: () => any;
    days: number | null;
    relatedUsers: User[];
}

export default function ReminderFormDialog(props: ReminderFormDialogProps) {
    const { reminderDialogOpen, onCancel, onSubmit, days, relatedUsers } = props;
    const { user } = useAuthentication();
    const { enqueueSnackbar } = useSnackbar();

    const [userList, setUserList] = useState<OptionItem<number>[]>([]);
    const apiUser = new ServiceApi<User>('user');

    useEffect(() => {
        onSearchUser('').then(setUserList);
    }, []);

    async function onSearchUser(search: string, selectedUsers?: number[]) {
        const where: WhereCondition<User> = [['active', '=', true], ['type', '=', UserType.Commbox], ['name', 'ilike', `%${search}%`]];
        if (selectedUsers && selectedUsers.length > 0) {
            where.push(['id', 'notIn', selectedUsers]);
        }
        const users = await apiUser.getList({ where, limit: 100, order: { name: 'ASC' } });
        if (selectedUsers && selectedUsers.length > 0) {
            const usersAux = await apiUser.getList({ where: [['id', 'in', selectedUsers]] });
            users.unshift(...usersAux);
        }
        return users.map((u) => {
            return { value: u.id, label: u.name };
        });
    }

    function onSubmitHandler(data: ReminderFormDialogData) {
        if (data.reminder_date && data.user_ids.length > 0) {
            onSubmit(data.comment, data.reminder_date, data.user_ids);
        } else {
            enqueueSnackbar('Data e atribuição são obrigatórios', { variant: 'error' });
        }
    }

    function getValidationSchema() {
        const validationSchema: FormValidationSchema<ReminderFormDialogDataFull> = {
            reminder_date: Yup
                .date()
                .required()
                .label('Data'),
            reminder_time: Yup
                .string()
                .required()
                .label('Horário')
        }
        return validationSchema;
    }

    function getInitialValues(): ReminderFormDialogDataFull {
        //reminder_date: fns.addDays(new Date(), days || 0),
        //console.log('days', days);
        return {
            comment: '',
            reminder_date: days ? fns.addDays(new Date(), days) : null,
            reminder_time: '00:00',
            user_ids: [{ label: user?.name ?? '', value: user?.id ?? 0 }]
        };
    }

    return (
        <FormDialog<ReminderFormDialogDataFull>
            show={reminderDialogOpen}
            title={'Novo lembrete'}
            dismiss={() => null}
            onSubmit={(values, dirty, errors) => {
                if (errors) {
                    for (var error of Object.values(errors).filter(e => !!e)) {
                        enqueueSnackbar(error, { variant: 'error' });
                    }
                    return;
                }
                const { comment, reminder_date, reminder_time, user_ids } = values;

                if (!reminder_date || !reminder_time) return;

                const horario = reminder_time.split(':');

                //valida se horario é valido
                if (horario.length != 2 || !fns.isValid(fns.set(reminder_date, {
                    hours: parseInt(horario[0]),
                    minutes: parseInt(horario[1]),
                    seconds: 0,
                    milliseconds: 0
                }))) {
                    enqueueSnackbar('Horário inválido', { variant: 'error' });
                    return;
                }

                const date = fns.set(reminder_date, {
                    hours: parseInt(horario[0]),
                    minutes: parseInt(horario[1]),
                    seconds: 0,
                    milliseconds: 0
                });

                // console.log('date', date, parseInt(horario[0]), parseInt(horario[1]));

                onSubmitHandler({ comment, reminder_date: date, user_ids });
            }}
            initialValues={getInitialValues()}
            enableReinitialize={true}
            validationSchema={getValidationSchema()}
            maxWidth='md'
            fullWidth
        >
            {({ values, errors, handleChange, setFieldValue, isSubmitting, dirty }) => (
                <>
                    <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <TextField
                                name="comment"
                                label={"Lembrete"}
                                margin="dense"
                                variant="outlined"
                                fullWidth
                                multiline
                                autoFocus
                                inputProps={{
                                    style: {
                                        resize: 'vertical', overflow: 'auto', height: 165, minHeight: 165, maxHeight: 400
                                    }
                                }}
                                value={values.comment}
                                onChange={handleChange}
                                error={!!errors.comment && !!dirty.comment}
                                helperText={!!errors.comment && !!dirty.comment && errors.comment}
                            />
                        </Grid>
                    </Grid>
                    <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <SelectSearchMultiAsync<number>
                                placeholder="Atribuído para"
                                label="Atribuído para"
                                loadOptions={(s, ss) => onSearchUser(s, ss?.map(as => as.value) || values.user_ids.map(as => as.value))}
                                value={values.user_ids || []}
                                onChangeHandle={async (_, value) => {
                                    await setFieldValue('user_ids', value);
                                }}
                                name='assigned_users'
                                defaultOptions={userList}
                                fullWidth
                                margin='dense'
                                error={!!errors.user_ids && !!dirty.user_ids}
                                helperText={!!errors.user_ids && !!dirty.user_ids && errors.user_ids}
                            />
                        </Grid>
                        {relatedUsers && <Grid item xs={12} sm={12} md={12} lg={12} xl={12} style={{ padding: '0 0 5px 5px', display: 'ruby' }}>
                            {relatedUsers.map((u, ii) => {
                                return (
                                    <React.Fragment key={`related-user-${u.id}`}>
                                        <div style={{ cursor: 'pointer', color: '#007CFF' }} onClick={async () => {
                                            if (values.user_ids.find(o => o.value === u.id)) return;
                                            await setFieldValue('user_ids', [...(values.user_ids || []), { value: u.id, label: u.name }]);
                                        }}>{u.name}</div>
                                        {ii < relatedUsers.length - 1 ? <>, </> : null}
                                    </React.Fragment>
                                );
                            })}
                        </Grid>}
                    </Grid>
                    <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                            <DatePicker
                                label={'Data'}
                                value={values.reminder_date}
                                onChange={(value) => setFieldValue('reminder_date', value)}
                                required
                                style={{ width: '150px' }}
                            />
                            <TimePicker
                                label={'Horário'}
                                value={values.reminder_time}
                                onChange={(value) => setFieldValue('reminder_time', value)}
                                required
                                name='reminder_time'
                                style={{ width: '80px', marginLeft: 10 }}
                            />
                        </Grid>
                    </Grid>
                    <FormControl fullWidth variant="outlined" margin="dense" style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                        <ErrorButton
                            size='small'
                            variant="contained"
                            disabled={isSubmitting}
                            onClick={() => onCancel()}
                        >
                            Cancelar
                        </ErrorButton>
                        <DefaultButton
                            size='small'
                            type='submit'
                            variant="contained"
                            disabled={isSubmitting}
                            style={{ marginLeft: 10 }}>
                            OK
                        </DefaultButton>
                    </FormControl>
                </>
            )}
        </FormDialog>
    );
}